import { Link, router } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";

import CurrentUserAvatar from "components/shared/CurrentUserAvatar";
import NotificationIndicator from "features/Nav/components/Nav/parts/NotificationIndicator";
import Text from "hardcover-ui/components/Text";
import useCurrentUser from "hooks/fragments/useCurrentUser";
import { useIsTouchDevice } from "hooks/useIsTouchDevice";
import owned from "icons/solid/backpack.svg";
import reading from "icons/solid/book-open-cover.svg";
import read from "icons/solid/book-sparkles.svg";
import wanttoread from "icons/solid/bookmark.svg";
import goals from "icons/solid/bullseye-arrow.svg";
import stats from "icons/solid/chart-mixed.svg";
import prompts from "icons/solid/comment-dots.svg";
import settings from "icons/solid/gear.svg";
import reviews from "icons/solid/message-heart.svg";
import activity from "icons/solid/newspaper.svg";
import lists from "icons/solid/rectangle-history.svg";
import network from "icons/solid/user-group.svg";
import userIcon from "icons/solid/user.svg";

import DropdownIndicator from "../DropdownIndicator";

import MenuNotifications from "./parts/MenuNotifications";
import MenuSignOut from "./parts/MenuSignOut";
import MenuThemeChanger from "./parts/MenuThemeChanger";
import UserMenuItem from "./parts/UserMenuLink";

export default function UserMenu() {
  const username = useCurrentUser(u => u.username);
  const isTouch = useIsTouchDevice();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleStart = () => {
      setIsMenuOpen(false);
      if (userMenuRef.current) {
        const focusedChild =
          userMenuRef.current.querySelector<HTMLElement>(":focus");
        if (focusedChild && typeof focusedChild.blur === "function") {
          focusedChild.blur();
        }
      }
    };

    router.on("start", handleStart);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="group/usermenu relative -m-2 p-1" ref={userMenuRef}>
      <Link
        href={`/@${username}`}
        className="bg-white rounded-full flex mt-1 hover:outline-hidden hover:ring-2 hover:ring-offset hover:ring-offset-gray-100 dark:hover:ring-offset-gray-800 hover:ring-white"
        onClick={e => {
          if (isTouch) {
            e.preventDefault();
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }
        }}
      >
        <span className="sr-only">Open user menu</span>
        <NotificationIndicator />
        <CurrentUserAvatar size="md" isLink />
      </Link>

      <div
        className={`absolute top-8 -mt-px -right-4 z-20 pointer-events-none ${
          isTouch
            ? isMenuOpen
              ? "block"
              : "hidden"
            : "hidden group-hover/usermenu:block group-focus-within/usermenu:block"
        }`}
      >
        <DropdownIndicator className="w-20" />
      </div>

      <div
        className={`-m-3 p-2 absolute top-16 right-1 ${
          isTouch
            ? isMenuOpen
              ? "block"
              : "hidden"
            : "hidden group-hover/usermenu:block group-focus-within/usermenu:block"
        }`}
      >
        <div className="-mt-1 min-w-max divide-y divide-secondary ring-2 dark:ring-gray-700 dark:bg-gray-900 ring-gray-200 bg-background rounded-lg shadow-lg overflow-hidden">
          <div className="min-w-full flex flex-col p-2">
            {isTouch && (
              <UserMenuItem
                icon={userIcon}
                text="Profile"
                url={`/@${username}`}
              />
            )}
            <MenuNotifications />
          </div>
          <div className="min-w-full flex flex-col p-2">
            <Text variant="secondary" size="sm" as="p">
              Books
            </Text>
            <UserMenuItem
              icon={wanttoread}
              url={`/@${username}/books/want-to-read`}
              text="Want to read"
            />
            <UserMenuItem
              icon={reading}
              url={`/@${username}/books/currently-reading`}
              text="Currently reading"
            />
            <UserMenuItem
              icon={read}
              url={`/@${username}/books/read`}
              text="Read"
            />
            <UserMenuItem
              icon={owned}
              url={`/@${username}/lists/owned`}
              text="Owned"
            />
          </div>
          <div className="min-w-full flex flex-col p-2">
            <UserMenuItem
              icon={stats}
              url={`/@${username}/stats`}
              text="Stats"
            />
            <UserMenuItem
              icon={reviews}
              url={`/@${username}/reviews`}
              text="Reviews"
            />
            <UserMenuItem
              icon={lists}
              url={`/@${username}/lists`}
              text="Lists"
            />
            <UserMenuItem
              icon={prompts}
              url={`/@${username}/prompts/answered`}
              text="Prompts"
            />
            <UserMenuItem
              icon={goals}
              url={`/@${username}/goals`}
              text="Goals"
            />
            <UserMenuItem
              icon={network}
              url={`/@${username}/network/following`}
              text="Network"
            />
            <UserMenuItem
              icon={activity}
              url={`/@${username}/activity`}
              text="Activity"
            />
          </div>
          <div className="min-w-full flex flex-col p-2 items-start">
            <MenuThemeChanger />
            <UserMenuItem icon={settings} url="/account" text="Settings" />
          </div>

          <div className="min-w-full flex flex-col p-2">
            <MenuSignOut />
          </div>
        </div>
      </div>
    </div>
  );
}
